import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CustomerMode,
  CustomerTypes,
  IBooking,
  ICustomer, ICustomerVenueDetails,
  IDiaryVenue,
  ISearchingCustomer,
} from 'shared-types/index';
import { ICustomerState } from './types';
import { CustomerDetailType } from 'design-v2/common/CustomerTab/types';
import { CustomerGeneratorService } from 'external-services/customer-generator-service';
import lodash, { cloneDeep, orderBy } from 'lodash-es';
import CustomerLookupService from 'external-services/customer-lookup-service/index';
import BookingLookupService from 'external-services/booking-lookup-service/index';
import BookingValidationService from 'external-services/booking-validation-service/index';
import { DiaryRootState } from 'appRoot/rootReducer';
import ReportingService from 'external-services/ipos-reporting-service/index';
import { IntlService } from 'external-services/intl-service/index';
import CustomerNormaliseService from 'external-services/customer-normalise-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import CustomerUpdateService from 'external-services/customer-update-service/customerUpdate.service';
import CustomerSearchService from 'external-services/customer-search-service';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import StatusConfigService from 'external-services/status-config-service';
// import store from 'appRoot/Store';

const NS = 'customerSlice';

export const initialState: ICustomerState = {
  customer: {} as ICustomer,
  originalCustomer: {} as ICustomer,
  customerLabel: '',
  venueVisitHistory: [],
  customerBookings: [],
  filteredCustomerBookings: [],
  isErrorLoadHistory: false,
  avgSpendAmount: '',
  totalVisitCount: 0,
  countryCodePrefix: '',
  phoneWithoutCountryCode: '',
  phoneTouched: false,
  customerTouched: false,
  customerView: CustomerMode.SEARCH,
  duplicateCustomer: false,
  skipCustomerDuplicationCheck: false,
  customers: [],
  searchingCustomer: {} as ICustomer,
  showMoreExpanded: false,
  expandedCustomer: true,
  expandedBookedBy: false,
  forceUpdateCustomer: false,
};

export const updateVenueVisitHistory = createAsyncThunk(
  'customer/updateVenueVisitHistory',
  async (payload: { customerId: string }, { dispatch, getState }): Promise<string[]> => {
    let histories: string[] = [];

    try {
      histories = (await CustomerLookupService.getVenueVisitHistory(payload.customerId)) || [];
    } catch (err) {
      console.error(NS, `"CustomerLookupService.getVenueVisitHistory" failed`, err);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem loading venue history',
          errorMessage: `There was an error trying to load venue history. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(err),
        })
      );
    }

    return histories;
  }
);

export const updateTotalVisitCount = createAsyncThunk(
  'customer/updateTotalVisitCount',
  async (payload: { customerId: string }, { dispatch, getState }): Promise<number> => {
    let totalVisit = 0;

    try {
      const customerBookingList =
        (await BookingLookupService.getBookingsByCustomerIds([payload.customerId], true)) || [];

      const customerBooking = customerBookingList.filter((booking) =>
        BookingValidationService.isValidPastBooking(booking)
      );
      totalVisit = customerBooking.length;
    } catch (err) {
      //could not replicate the error
      console.warn(NS, `"updateTotalVisitCount" failed`, err);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem getting total visits count',
          errorMessage: `There was a problem getting the total visits count. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(err),
        })
      );
    }

    return totalVisit;
  }
);
/**
 * this function may like toVenueCustomer func in customerDetails.directive.js in Diary,
 * but I only use part of them, which is for managerNotes.
 * please add more if you need other info.
 */
const passVenueCustomerInfoToCustomer = (venueDetail: ICustomerVenueDetails, customer: ICustomer) => {
  customer.managerNotes = venueDetail?.managerNotes;
  customer.membershipNumber = venueDetail?.membershipNumber;
};

export const loadCustomer = createAsyncThunk(
  'customer/loadCustomer',
  async (
    payload: { customerId: string; ignoreSubscribed: boolean },
    { dispatch, getState }
  ): Promise<{
    customer: ICustomer;
    ignoreSubscribed: boolean;
  }> => {
    let customer: ICustomer;
    const { venueReducer, accountReducer, customerReducer } = getState() as DiaryRootState;
    const { id } = venueReducer;
    const { isManager, hasAccountCustomerAccess } = accountReducer;
    const { customerView } = customerReducer;
    try {
      // Get customer details by current venue

      /*
        for "restricted venue users" and "venue users" - CustomerLookupService.getById returns an error 404 if customer not found in that venue
        We also cant use the account level api cause restricted users are unauthorised
      */
      customer = await CustomerLookupService.getById(payload.customerId, !hasAccountCustomerAccess);


      // venueDetail contains the venues where customer is present. if its null means that for the current venue selected customer doesnt exist.
      const venueDetail = customer?.customerVenueDetails?.find((v: ICustomerVenueDetails) => v?.venueId === id);

      /*
        for restricted venue users (isManager is false) the CustomerLookupService.getById returns null customer if the customer is not present in the venue
        so if a customer was selected from the customer search view we need to update the selected customer for this venue.
        customerView will be equal to CustomerMode.DISPLAY if a customer was selected from the CustomerMode.SEARCH view.
      */
      if (!customer && !isManager && customerView === CustomerMode.DISPLAY) {
        dispatch(actions.setForceUpdateCustomer(true));
      }

      /**
       * for isManager true: CustomerLookupService.getById will return all customer for the account.
       * if selected customer not present in the venue we should update the customer object for that venue
       * Hence setting a variable to setForceUpdateCustomer which will update the customer in saveNewBooking function in validation\slice
       */

      // if venueDetail is undefined - means the customer is not present in the current venue.
      if (!venueDetail && isManager) {
        dispatch(actions.setForceUpdateCustomer(true))// since the customer is not present we need to update the customer through api
      }
      if (venueDetail && isManager) {
        passVenueCustomerInfoToCustomer(venueDetail, customer);
      }
    } catch (err) {
      console.error(NS, 'CustomerLookupService.getById failed', err);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem fetching customers',
          errorMessage: `There was an error while fetching customers. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(err),
        })
      );
      console.warn(NS, `"CustomerLookupService.getById" failed`, err);
    }

    return { customer, ignoreSubscribed: payload.ignoreSubscribed };
  }
);

export const loadInlineCustomer = createAsyncThunk(
  'customer/loadInlineCustomer',
  async (payload, { dispatch, getState }): Promise<ICustomer> => {
    const {
      displayReducer: { activeBooking },
    } = getState() as DiaryRootState;

    const customer: ICustomer = {
      firstName: activeBooking.firstName,
      lastName: activeBooking.lastName,
      phone: activeBooking.phone,
      email: activeBooking.email, // Horita not sure , but currently there is email input for walkin
      customerType: CustomerTypes.inline,
    } as any as ICustomer;

    return customer;
  }
);

export const getCustomerHistory = createAsyncThunk(
  'customer/getCustomerHistory',
  async (
    payload: { customerId: string },
    { dispatch, getState }
  ): Promise<{ customerBookings: IBooking[]; filteredCustomerBookings: IBooking[] }> => {
    const { statusReducer } = getState() as DiaryRootState;
    const { fullStatusOptions } = statusReducer;

    const customerId: string = payload.customerId;

    const showError = (err: any) => {
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem loading customer history',
          errorMessage: `There was an error while loading the customer history for this booking. \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(err),
        })
      );
    };

    return new Promise((resolve, reject) => {
      if (customerId) {
        BookingLookupService.getBookingsByCustomerIds([customerId], true)
          .then((customerBookings: IBooking[]) => {
            const customerBookingList: IBooking[] = orderBy(
              customerBookings,
              ['time'],
              ['desc']
            );

            const updatedCustomerBookings =
              StatusConfigService.updatedBookingsBasedOnStatusChange(
                cloneDeep(customerBookingList),
                fullStatusOptions
              );

            const filteredCustomerBookings = updatedCustomerBookings.filter((booking) =>
              BookingValidationService.isValidPastBooking(booking)
            );
            resolve({
              customerBookings: updatedCustomerBookings,
              filteredCustomerBookings: filteredCustomerBookings,
            });
          })
          .catch((err: any) => {
            console.error(
              'Load Booking: Get Customer Booking History',
              customerId,
              err
            );
            showError(err);
            reject(err);
          });
      } else {
        resolve({
          customerBookings: [],
          filteredCustomerBookings: [],
        });
      }
    });
  }
);

export const getCustomerAmountDetails = createAsyncThunk(
  'customer/getCustomerAmountDetails',
  async (payload, { dispatch, getState }): Promise<string> => {
    const {
      customerReducer: { customer },
      venueReducer: { id, currency },
    } = getState() as DiaryRootState;

    if (customer && customer._id) {
      const average = await ReportingService.getCustomerAmountDetails(id, customer._id);
      /**
       * @todo
       * need to implement currency for venue detail
       */
      const avgSpendAmount = IntlService.currencyValueAsString(average, currency); //venue.currency
      return avgSpendAmount;
    }
    return '';
  }
);
export const updateCustomer = createAsyncThunk(
  'customer/updateCustomer',
  async (payload, { dispatch, getState, rejectWithValue }): Promise<ICustomer> => {
    const {
      customerReducer: { customer },
    } = getState() as DiaryRootState;
    try {
      if (customer && customer._id) {
        return await CustomerUpdateService.update(customer);
      }
    } catch (e) {
      rejectWithValue(e);
      console.log('updateCustomer error', e);
    }
    return undefined;
  }
);
export const checkDuplicatedCustomer = createAsyncThunk(
  'customer/checkDuplicatedCustomer',
  async (
    payload,
    { dispatch, getState }
  ): Promise<{
    customers: ICustomer[];
    customerView: CustomerMode;
    duplicateCustomer: boolean;
    customer: ICustomer;
  }> => {
    const {
      customerReducer: { customer },
      venueReducer: venue,
    } = getState() as DiaryRootState;
    let result: any = [];
    if (!customer.phone && !customer.email) {
      return {
        customers: [],
        customerView: CustomerMode.SEARCH,
        duplicateCustomer: false,
        customer: customer,
      };
    }
    try {
      result = await CustomerSearchService.searchCustomer(customer, venue as IDiaryVenue);
    } catch (err) {
      console.error('error checkDuplicatedCustomer', err);
    }
    if (result && result.length > 0) {
      return {
        customers: result,
        customerView: CustomerMode.SEARCH,
        duplicateCustomer: true,
        customer: customer,
      };
    }
    return {
      customers: [],
      customerView: CustomerMode.SEARCH,
      duplicateCustomer: false,
      customer: customer,
    };
  }
);

const customerSlice = createSlice({
  name: 'customer', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    resetCustomer: (state: ICustomerState) => {
      state.customer = {} as ICustomer;
      state.phoneWithoutCountryCode = '';
    },
    resetCustomerToOriginal: (state: ICustomerState) => {
      state.customer = state.originalCustomer;
    },
    setCustomer: (state: ICustomerState, action: PayloadAction<ICustomer>) => {
      state.customer = action.payload || ({} as ICustomer);
      state.originalCustomer = lodash.cloneDeep(action.payload || ({} as ICustomer));
      state.customerLabel = CustomerGeneratorService.getDisplayName(state.customer, true);
    },
    // for customer search
    setCustomers: (state: ICustomerState, action: PayloadAction<ICustomer[]>) => {
      state.customers = action.payload;
    },
    setSearchingCustomer: (state: ICustomerState, action: PayloadAction<ISearchingCustomer>) => {
      state.searchingCustomer = action.payload;
    },
    setPhoneWithoutCountryCode: (state: ICustomerState, action: PayloadAction<string>) => {
      state.phoneWithoutCountryCode = action.payload;
    },
    updateCustomerDetail: (
      state: ICustomerState,
      action: PayloadAction<{ type: CustomerDetailType; value: any }>
    ) => {
      const isEmptyBirthday =
        action.payload.type === CustomerDetailType.birthday && action.payload.value === '0-0-0000';
      if (!isEmptyBirthday) {
        state.customerTouched = true;
      }
      if (action.payload.type === CustomerDetailType.birthday) {
        CustomerNormaliseService.updateCustomerBirthdayByString(
          state.customer,
          action.payload.value
        );
      } else {
        // @ts-ignore
        state.customer[action.payload.type] = action.payload.value;
      }
    },
    setCustomerBookings: (state: ICustomerState, action: PayloadAction<IBooking[]>) => {
      state.customerBookings = action.payload;
    },
    setCustomerView: (state: ICustomerState, action: PayloadAction<CustomerMode>) => {
      if (action.payload === CustomerMode.DISPLAY) {
        state.skipCustomerDuplicationCheck = true;
      }
      state.customerView = action.payload;
    },
    setDuplicateCustomer: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.duplicateCustomer = action.payload;
    },
    setShowMoreExpanded: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.showMoreExpanded = action.payload;
    },
    setExpandedCustomer: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.expandedCustomer = action.payload;
    },
    setExpandedBookedBy: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.expandedBookedBy = action.payload;
    },
    setSkipCustomerDuplicationCheck: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.skipCustomerDuplicationCheck = action.payload;
    },
    setForceUpdateCustomer: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.forceUpdateCustomer = action.payload;
    },
    setCustomerPhone: (
      state: ICustomerState,
      action: PayloadAction<{
        countryCodePrefix: string;
        phoneWithoutCountryCode: string;
      }>
    ) => {
      const { countryCodePrefix, phoneWithoutCountryCode } = action.payload;
      state.countryCodePrefix = countryCodePrefix;
      state.phoneWithoutCountryCode = phoneWithoutCountryCode;
      state.customerTouched = true;
    },
    setPhoneTouched: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.phoneTouched = action.payload;
    },
    setCustomerTouched: (state: ICustomerState, action: PayloadAction<boolean>) => {
      state.customerTouched = action.payload;
    },
    setFilteredCustomerBookings: (state: ICustomerState, action: PayloadAction<IBooking[]>) => {
      state.filteredCustomerBookings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateVenueVisitHistory.fulfilled, (state: ICustomerState, action) => {
      state.venueVisitHistory = action.payload;
    });
    builder.addCase(updateCustomer.fulfilled, (state: ICustomerState, action) => {
      if (action.payload) {
        state.customer = action.payload;
      }
    });

    builder.addCase(updateTotalVisitCount.fulfilled, (state: ICustomerState, action) => {
      state.totalVisitCount = action.payload;
    });

    builder.addCase(getCustomerHistory.fulfilled, (state: ICustomerState, action) => {
      state.customerBookings = action.payload.customerBookings;
      state.filteredCustomerBookings = action.payload.filteredCustomerBookings;
      state.totalVisitCount = action.payload.filteredCustomerBookings.length;
      state.isErrorLoadHistory = false;
    });

    builder.addCase(getCustomerHistory.rejected, (state: ICustomerState, action) => {
      state.customerBookings = [];
      state.filteredCustomerBookings = [];
      state.isErrorLoadHistory = true;
    });

    builder.addCase(
      loadCustomer.fulfilled,
      (
        state: ICustomerState,
        action: PayloadAction<{
          customer: ICustomer;
          ignoreSubscribed: boolean;
        }>
      ) => {
        const subscribed: boolean = state.customer.subscribed;
        // fixing the issue https://nowbookit.atlassian.net/browse/NBI-8562?focusedCommentId=97920
        if (action.payload.customer) {
          state.customer = action.payload.customer;
          state.phoneWithoutCountryCode = '';
          state.countryCodePrefix = '';
          state.originalCustomer = lodash.cloneDeep(action.payload.customer || ({} as ICustomer));
          state.customerLabel = CustomerGeneratorService.getDisplayName(state.customer, true);
          if (action.payload.ignoreSubscribed) {
            state.customer.subscribed = subscribed;
            // state.originalCustomer.subscribed = subscribed;
          }
        }

      }
    );

    builder.addCase(
      loadInlineCustomer.fulfilled,
      (state: ICustomerState, action: PayloadAction<ICustomer>) => {
        state.customer = action.payload;
        state.countryCodePrefix = '';
        state.phoneWithoutCountryCode = '';
        state.phoneTouched = false;
        state.customerTouched = false;
        state.skipCustomerDuplicationCheck = true;
      }
    );

    builder.addCase(
      getCustomerAmountDetails.fulfilled,
      (state: ICustomerState, action: PayloadAction<string>) => {
        state.avgSpendAmount = action.payload;
      }
    );

    builder.addCase(
      checkDuplicatedCustomer.fulfilled,
      (
        state: ICustomerState,
        action: PayloadAction<{
          customers: ICustomer[];
          customerView: CustomerMode;
          duplicateCustomer: boolean;
          customer: ICustomer;
        }>
      ) => {
        if (action.payload.duplicateCustomer) {
          state.customers = action.payload.customers;
          state.customerView = action.payload.customerView;
          state.duplicateCustomer = action.payload.duplicateCustomer;
          if (action.payload.customerView === CustomerMode.SEARCH) {
            state.searchingCustomer = CustomerNormaliseService.customerToSearchingCustomer(
              action.payload.customer
            );
          }
        }
      }
    );
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = customerSlice;
// Extract and export each action creator by name
export { actions as customerActions };
// Export the reducer, either as a default or named export
export default reducer;
